"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.floorToNearest = exports.roundHalfAwayFromZero = exports.roundToNearest = exports.isBetweenNumbersInclusively = exports.isNotPositiveNumber = exports.round80To100TaxRule = exports.toNumOrZero = exports.divideBy100 = exports.areTwoNumbersCloseEnough = exports.abbreviateNumber = exports.formatNumberWithCommas = void 0;
const numeral_1 = __importDefault(require("numeral"));
const enums_1 = require("./enums");
const errors_1 = require("./errors");
const formatNumberWithCommas = (num) => (0, numeral_1.default)(num).format('0,0');
exports.formatNumberWithCommas = formatNumberWithCommas;
// Calculates significant figures with suffixes K/M/B/T, e.g. 1234 = 1.23K
const SUFFIXES = ['', 'K', 'M', 'B', 'T'];
const abbreviateNumber = ({ inputNumber, sigFigs = 3 }) => {
    const absoluteInputNumber = Math.abs(inputNumber);
    // Only assigns sig figs and suffixes for numbers > 1
    if (absoluteInputNumber <= 1) {
        const baseRound = absoluteInputNumber.toPrecision(sigFigs);
        return { abbreviation: baseRound, base: +baseRound };
    }
    // Calculate for numbers > 1
    const power10 = Math.log10(absoluteInputNumber);
    // 0 = '', 1 = 'K', 2 = 'M', 3 = 'B', 4 = 'T'
    // 100: power10 = 2, suffixNum = 0, suffix = ''
    // 1000: power10 = 3, suffixNum = 1, suffix = 'K'
    const suffixNum = Math.floor(power10 / 3);
    const suffix = SUFFIXES[suffixNum];
    // Would be 1 for '', 1000 for 'K', 1000000 for 'M', etc.
    const suffixPower10 = Math.pow(10, suffixNum * 3);
    const base = (absoluteInputNumber / suffixPower10) * Math.sign(inputNumber);
    const baseRound = base.toPrecision(sigFigs);
    return {
        abbreviation: `${baseRound}${suffix}`,
        base: +baseRound,
        suffix,
    };
};
exports.abbreviateNumber = abbreviateNumber;
const areTwoNumbersCloseEnough = (a, b, discrepancy = 0.01) => {
    if (a === b) {
        return true;
    }
    if (a === 0 || b === 0) {
        return false;
    }
    return Math.abs(a / b - 1) < discrepancy;
};
exports.areTwoNumbersCloseEnough = areTwoNumbersCloseEnough;
const divideBy100 = (a) => a / 100;
exports.divideBy100 = divideBy100;
const toNumOrZero = (a) => a ?? 0;
exports.toNumOrZero = toNumOrZero;
const round80To100TaxRule = ({ type, value }) => {
    if (value < 0) {
        throw (0, errors_1.createError)('Round80To100TaxRuleError', 'Must be 0 or greater');
    }
    if (type === enums_1.ValueType.PERCENT) {
        if (value > 100) {
            throw (0, errors_1.createError)('Round80To100TaxRuleError', 'Fractions must be within 100 or less');
        }
        return value >= 80 ? 100 : value;
    }
    if (type === enums_1.ValueType.FRACTION) {
        if (value > 1) {
            throw (0, errors_1.createError)('Round80To100TaxRuleError', 'Fractions must be 1 or less');
        }
        return value >= 0.8 ? 1.0 : value;
    }
    throw (0, errors_1.createError)(`Round80To100TaxRuleError: Invalid ValueType passed for this function: ${type}`);
};
exports.round80To100TaxRule = round80To100TaxRule;
const isNotPositiveNumber = (value) => isNaN(value) || value < 0;
exports.isNotPositiveNumber = isNotPositiveNumber;
const isBetweenNumbersInclusively = (value, min, max) => value >= min && value <= max;
exports.isBetweenNumbersInclusively = isBetweenNumbersInclusively;
const roundToNearest = (value, decimals) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};
exports.roundToNearest = roundToNearest;
/**
 * Rounds halves away from zero, the way Excel's ROUND() does: -0.5 becomes -1, where `roundToNearest` gives -0.
 * Use it to reproduce the result of a spreadsheet formula in TypeScript.
 */
const roundHalfAwayFromZero = (value, decimals) => {
    const factor = 10 ** decimals;
    return (Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor;
};
exports.roundHalfAwayFromZero = roundHalfAwayFromZero;
const floorToNearest = (value, decimals) => {
    const factor = 10 ** decimals;
    return Math.floor(value * factor) / factor;
};
exports.floorToNearest = floorToNearest;
